import React, { useEffect, useState } from 'react';
import { initTheme, toggleTheme } from '../utils/theme';
import LogoWhitePulse from "../images/logo/pulseCIO_blanc_logo.png";
import LogoBlackPulse from "../images/logo/pulseCIO_black_logo.png";
import { FiSun, FiMoon } from 'react-icons/fi';

const Navbar = () => {
  const [theme, setTheme] = useState('light');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const t = initTheme();
    setTheme(t);
  }, []);

  const handleToggle = () => {
    const next = toggleTheme(theme);
    setTheme(next);
  };

  const toggleMobile = () => {
    setMobileOpen((s) => !s);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md border-b border-gray-200/80 dark:border-white/[0.06]" aria-label="Barre de navigation principale">
      <div className="container mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <a href="/" className="inline-flex items-center gap-3" aria-label="Accueil PulseCIO">
            <img src={LogoBlackPulse} alt="Logo PulseCIO noir" className="h-8 md:h-6 lg:h-6 xl:h-8 block dark:hidden" />
            <img src={LogoWhitePulse} alt="Logo PulseCIO blanc" className="h-8 md:h-6 lg:h-6 xl:h-8 hidden dark:block" />
          </a>
        </div>

        <nav className="flex items-center">
          {/* Desktop links */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="#features" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Fonctionnalités</a>
            <a href="#galerie" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Galerie</a>
            <a href="#testimonials" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Témoignages</a>
            <a href="#plans" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Forfaits</a>
            <a href="#faq" className="text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors pr-2">FAQ</a>
            {/* Bouton Se connecter — contour */}
            <a
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-800 dark:text-white hover:border-brand-400 dark:hover:border-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
              href="https://app.pulsecio.com"
              target="_blank"
              rel="noreferrer"
              aria-label="Se connecter à PulseCIO"
            >
              Se connecter
            </a>
            {/* Bouton CTA principal — gradient brand */}
            <a
              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg gradient-brand text-white shadow-md hover:opacity-90 transition-opacity"
              href="#plans"
              aria-label="Voir les tarifs et essayer gratuitement"
            >
              Essayer gratuitement
            </a>
          </div>

          {/* Theme toggle (always visible) */}
          <button onClick={handleToggle} aria-label="Toggle theme" className="ml-3 w-9 h-9 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-white/[0.07] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.12] transition-colors">
            {theme === 'dark' ? <FiSun className='h-4 w-4' /> : <FiMoon className='h-4 w-4' />}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={toggleMobile}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            className="ml-2 inline-flex lg:hidden items-center justify-center w-9 h-9 rounded-lg bg-gray-100 dark:bg-white/[0.07] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.12] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 6h16M4 12h16M4 18h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </nav>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full bg-white/95 dark:bg-gray-950/95 backdrop-blur-md border-b border-gray-200 dark:border-white/[0.06] shadow-xl z-50">
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              <a href="#features" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Fonctionnalités</a>
              <a href="#galerie" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Galerie</a>
              <a href="#testimonials" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Témoignages</a>
              <a href="#plans" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">Forfaits</a>
              <a href="#faq" onClick={() => setMobileOpen(false)} className="py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-brand-600 dark:hover:text-brand-400 transition-colors">FAQ</a>
              <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-white/[0.06]">
                <a
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center px-4 py-2.5 text-sm font-semibold rounded-lg border border-gray-300 dark:border-white/20 text-gray-800 dark:text-white hover:border-brand-400 dark:hover:border-brand-500 transition-colors"
                  href="https://app.pulsecio.com"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Se connecter à PulseCIO"
                >
                  Se connecter
                </a>
                <a
                  onClick={() => setMobileOpen(false)}
                  className="w-full text-center px-4 py-2.5 text-sm font-semibold rounded-lg gradient-brand text-white shadow-sm hover:opacity-90 transition-opacity"
                  href="#plans"
                  aria-label="Voir les tarifs et essayer gratuitement"
                >
                  Essayer gratuitement
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
